import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { t } from '@lingui/macro';
import styled from 'styled-components';

import SelectArrow from '@rover/icons/svg/select-arrow.svg';
import { Box, Flex } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';
import type { AutocompleteSelectOption } from '@rover/types/src/AutocompleteSelectOption';

import { Props as InputProps, UnwrappedInput } from '../Input/Input';

import AutocompleteDropdown from './AutocompleteDropdown';
import SelectionButton from './SelectionButton';

type BorderWrapperProps = {
  isFocused: boolean;
  hasError?: boolean;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onClick?: () => void;
  children: React.ReactNode;
};

export const BorderWrapper: React.FC<BorderWrapperProps> = ({
  isFocused,
  hasError,
  className,
  onKeyDown,
  onClick,
  children,
  ...other
}) => {
  const { colors, background, borderWidth, radii } = useTheme();
  const borderColor = useMemo(() => {
    if (hasError && !isFocused) {
      return colors.border.error;
    }
    return isFocused ? colors.border.input_focus : colors.border.primary;
  }, [hasError, isFocused, colors]);
  return (
    <Flex
      webAs="ul"
      background="primary"
      borderStyle="solid"
      borderWidth="primary"
      borderColor={borderColor}
      color="primary"
      flexWrap="wrap"
      height="auto"
      onClick={onClick}
      webAsProps={{
        onKeyDown,
      }}
      minHeight="10x"
      paddingLeft="1x"
      position="relative"
      className={className}
      margin="0x"
      width="100%"
      sx={{
        backgroundColor: background.primary,
        border: `${borderWidth.primary} solid`,
        borderColor,
        borderRadius: radii.secondary,
        color: colors.text.primary,
        display: 'flex',
        flexWrap: 'wrap',
        height: 'auto',
        listStyleType: 'none',
        transition: 'border-color 0.15s ease-in-out',
      }}
      {...other}
    >
      {children}
    </Flex>
  );
};

const StyledTextInput = styled(UnwrappedInput)`
  background-color: transparent;
  border: 0 !important;
  flex: 1 0 0px;
  max-width: 100%;

  &:focus {
    border: 0;
    outline: 0;
  }

  ${MQ.XS.toString()} {
    &:focus {
      font-size: 16px;
    }
  }
`;

const ShadowSpan = React.forwardRef<
  HTMLSpanElement,
  Omit<React.ComponentPropsWithoutRef<'span'>, 'role'>
>((props, ref) => (
  <Box
    webAs="span"
    ref={ref}
    sx={{
      left: '-9999px',
      position: 'absolute',
      top: '-9999px',
    }}
    {...props}
    style={undefined}
  />
));

const StyledSelectArrow: React.FC = () => (
  <Box
    as={SelectArrow}
    sx={{
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      pointerEvents: 'none',
    }}
  />
);

export type Props = {
  id?: string;
  onSelectOption: (value: AutocompleteSelectOption['value']) => void;
  onUnselectOption: (value: AutocompleteSelectOption['value']) => void;
  onBlur?: () => void;
  options: AutocompleteSelectOption[];
  name: string;
  hasError?: boolean;
  selectedValues: ReadonlyArray<AutocompleteSelectOption['value']>;
  inputProps?: Partial<InputProps>;
  className?: string;
  required?: boolean;
};

type State = {
  isFocused: boolean;
  focusedDropdownIndex: number;
  focusedSelectionIndex: number;
  inputValue: string;
  inputMinWidth: number;
};

const initialState: State = {
  isFocused: false,
  focusedDropdownIndex: -1,
  focusedSelectionIndex: 0,
  inputValue: '',
  inputMinWidth: 0,
};

export const AutocompleteMultiSelect: React.FC<Props> = ({
  id = '',
  onSelectOption,
  onUnselectOption,
  onBlur,
  options,
  name,
  hasError,
  selectedValues = [],
  inputProps = {},
  className,
}) => {
  // State hooks
  const [isFocused, setIsFocused] = useState(false);
  const [focusedDropdownIndex, setFocusedDropdownIndex] = useState(-1);
  const [focusedSelectionIndex, setFocusedSelectionIndex] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [inputMinWidth, setInputMinWidth] = useState(0);

  // Ref hooks
  const inputRef = useRef<HTMLInputElement | null>(null);
  const shadowSpanRef = useRef<HTMLSpanElement | null>(null);

  // Helper functions

  const getSelectedOptions = useCallback((): AutocompleteSelectOption[] => {
    return selectedValues.map(
      (selectedValue) =>
        options.find(({ value }) => selectedValue === value) || {
          title: selectedValue.toString(),
          value: selectedValue,
          id: `${selectedValue}`.toLowerCase().replace(/[^a-z]/g, '-'),
        }
    );
  }, [options, selectedValues]);

  const getAutocompleteOptions = useCallback((): AutocompleteSelectOption[] => {
    if (!isFocused) return [];
    const lowerInputValue = inputValue.toLowerCase();
    return options.filter(
      (option) =>
        !selectedValues.includes(option.value) &&
        option.title.toLowerCase().includes(lowerInputValue)
    );
  }, [options, selectedValues, isFocused, inputValue]);

  const focusInput = useCallback((): void => {
    inputRef.current?.focus();
  }, []);

  const resizeInput = useCallback((): void => {
    // set min-width to jump us down a flex row when text overflows input
    // higher int below makes input 'jumping' trigger sooner
    if (shadowSpanRef.current) {
      setInputMinWidth(shadowSpanRef.current.offsetWidth + 30);
    }
  }, []);

  const handleReset = useCallback(
    (stateOverrides: Partial<State> = {}): void => {
      setIsFocused(stateOverrides.isFocused ?? initialState.isFocused);
      setFocusedDropdownIndex(
        stateOverrides.focusedDropdownIndex ?? initialState.focusedDropdownIndex
      );
      setFocusedSelectionIndex(stateOverrides.focusedSelectionIndex ?? selectedValues.length);
      setInputValue(stateOverrides.inputValue ?? initialState.inputValue);
      setInputMinWidth(stateOverrides.inputMinWidth ?? initialState.inputMinWidth);
    },
    [selectedValues.length]
  );

  const handleFocus = useCallback((): void => {
    const selectedValueCount = selectedValues.length;
    // on focus, populate dropdown with all options
    setIsFocused(true);
    setFocusedSelectionIndex(selectedValueCount);
    setFocusedDropdownIndex(0);
  }, [selectedValues.length]);

  const handleBlur = useCallback((): void => {
    handleReset();
    onBlur?.();
  }, [handleReset, onBlur]);

  const handleOptionClick = useCallback(
    (value: string | number): void => {
      onSelectOption(value);
      handleReset({
        focusedSelectionIndex,
      });
    },
    [onSelectOption, handleReset, focusedSelectionIndex]
  );

  const incrementFocusedSelectionIndex = useCallback(
    (diff: number): void => {
      setFocusedSelectionIndex((previousIndex) => {
        const lastIndex = selectedValues.length;
        let nextIndex = previousIndex + diff;
        if (nextIndex < 0) {
          nextIndex = 0;
        }
        if (nextIndex > lastIndex) {
          nextIndex = lastIndex;
        }
        if (nextIndex > previousIndex && nextIndex === lastIndex) {
          setFocusedDropdownIndex(-1);
        }
        return nextIndex;
      });
    },
    [selectedValues.length]
  );

  const incrementFocusedDropdownIndex = useCallback(
    (diff: number): void => {
      const autocompleteOptions = getAutocompleteOptions();
      setFocusedDropdownIndex((previousIndex) => {
        const lastIndex = autocompleteOptions.length - 1;
        let nextIndex = previousIndex + diff;
        if (nextIndex < -1) {
          nextIndex = -1;
        }
        if (nextIndex > lastIndex) {
          nextIndex = lastIndex;
        }
        return nextIndex;
      });
    },
    [getAutocompleteOptions]
  );

  const handleInputChange = useCallback((value: string): void => {
    setInputValue(value);
    setFocusedDropdownIndex(0);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      const caretOnStart =
        inputRef.current?.selectionStart === 0 && inputRef.current?.selectionEnd === 0;

      // handle the case when a selected option is focused separately from when the autocomplete is focused
      if (focusedSelectionIndex < selectedValues.length) {
        if (e.key !== 'Tab') {
          // only allow tab and the keys below when a selected option is focused
          e.preventDefault();
        }
        if (e.key === 'Backspace' || e.key === 'Delete' || e.key === ' ' || e.key === 'Enter') {
          // pressing Backspace, Delete or Space when a selected option is focused, unselects the option
          onUnselectOption(selectedValues[focusedSelectionIndex]);
        } else if (e.key === 'ArrowLeft') {
          // pressing left arrow focuses the previous selected option
          incrementFocusedSelectionIndex(-1);
        } else if (e.key === 'ArrowRight') {
          // pressing right arrow focuses the next selected option
          incrementFocusedSelectionIndex(1);
        }
      } else if (e.key === 'ArrowLeft') {
        if (caretOnStart) {
          // pressing left when caret is on the start of the input focuses the last selected option
          incrementFocusedSelectionIndex(-1);
          e.preventDefault();
        }
      } else if (e.key === 'Backspace') {
        // pressing backspace when caret is on the start of the input unselects the last selected option
        if (caretOnStart) {
          onUnselectOption(selectedValues[focusedSelectionIndex - 1]);
          incrementFocusedSelectionIndex(-1);
          e.preventDefault();
        }
      } else if (e.key === 'ArrowUp') {
        // go up in dropdown
        incrementFocusedDropdownIndex(-1);
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        // go down in dropdown
        incrementFocusedDropdownIndex(1);
        e.preventDefault();
      } else if (e.key === 'Escape') {
        // if list is displayed, close the list, else clear the textbox
        const autocompleteOptions = getAutocompleteOptions();
        if (autocompleteOptions.length > 0 && focusedDropdownIndex >= 0) {
          setFocusedDropdownIndex(-1);
        } else {
          setInputValue('');
          setFocusedDropdownIndex(-1);
        }
      } else if (e.key === 'Enter') {
        // bail if no autocompleteOptions, avoids errors
        const autocompleteOptions = getAutocompleteOptions();
        if (autocompleteOptions.length === 0) return;
        // bail if no option hovered/focused, avoids empty breed selections
        if (focusedDropdownIndex === -1) return;

        onSelectOption(autocompleteOptions[focusedDropdownIndex].value);
        // clean up input + autocomplete, leave field 'focused'
        handleReset({
          isFocused: true,
          focusedSelectionIndex: focusedSelectionIndex + 1,
          focusedDropdownIndex: -1,
        });
        e.preventDefault();
      }
    },
    [
      focusedSelectionIndex,
      selectedValues,
      onUnselectOption,
      incrementFocusedSelectionIndex,
      incrementFocusedDropdownIndex,
      getAutocompleteOptions,
      focusedDropdownIndex,
      onSelectOption,
      handleReset,
    ]
  );

  const handleOnClick = useCallback((isExpanded: boolean | string): void => {
    isMobileBrowser() && !isExpanded && inputRef.current?.blur();
  }, []);

  // Effects
  useEffect(() => {
    // if the input field is staying focused,
    // and we just cleared the inputValue, focus the <input />
    if (isFocused && inputValue.length === 0) {
      focusInput();
    }
  }, [inputValue, isFocused, focusInput]);

  useEffect(() => {
    // if inputValue length changed, resize the field
    resizeInput();
  }, [inputValue.length, resizeInput]);

  // Computed values
  const autocompleteOptions = getAutocompleteOptions();
  const selectedOptions = getSelectedOptions();
  const noOptionsAvailable = autocompleteOptions.length === 0 ? inputValue : '';
  const showOptions = focusedSelectionIndex === selectedValues.length && focusedDropdownIndex >= 0;
  const optionsToShow = showOptions ? autocompleteOptions : [];
  const isExpanded = noOptionsAvailable || optionsToShow.length > 0;

  // Memoized popup IDs
  const mainPopUpId = useMemo(() => 'breeds-autocomplete-popup', []);

  const ariaOwns = useMemo(
    () =>
      selectedOptions.map(({ id: optionId }) => `breeds-autocomplete-popup-${optionId}`).join(','),
    [selectedOptions]
  );

  const focusedId = useMemo(() => {
    if (focusedSelectionIndex < selectedValues.length) {
      return `breeds-autocomplete-popup-${selectedOptions[focusedSelectionIndex].id}`;
    }
    if (focusedDropdownIndex >= 0 && focusedDropdownIndex < autocompleteOptions.length) {
      return `breeds-autocomplete-popup-${autocompleteOptions[focusedDropdownIndex].id}`;
    }
    return '';
  }, [
    focusedSelectionIndex,
    selectedValues.length,
    selectedOptions,
    focusedDropdownIndex,
    autocompleteOptions,
  ]);

  return (
    <BorderWrapper
      className={className}
      isFocused={isFocused}
      hasError={hasError}
      onKeyDown={handleKeyDown}
      onClick={focusInput}
    >
      {selectedOptions.map((option, index) => {
        const { title, value, id: optionId } = option;
        return (
          <SelectionButton
            id={`breeds-autocomplete-popup-${optionId}`}
            role="option"
            highlighted={isFocused && index === focusedSelectionIndex}
            onClickIcon={(): void => onUnselectOption(value)}
            key={optionId}
            ariaLabel={t`Remove ${title}`}
          >
            {title}
          </SelectionButton>
        );
      })}
      <StyledTextInput
        data-testid="autocomplete-input"
        id={id} // comes from LabelAndErrorFormGroup and should apply to the input, not component
        type="text"
        name={name}
        inputRef={(node: HTMLInputElement | null): void => {
          inputRef.current = node;
        }}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onClick={handleOnClick(isExpanded)}
        role="combobox"
        aria-expanded={isExpanded}
        aria-controls={mainPopUpId}
        aria-autocomplete="list"
        aria-activedescendant={focusedId}
        aria-owns={mainPopUpId}
        title={selectedOptions.map(({ title }) => title).join(', ')}
        style={{
          minWidth: `${inputMinWidth}px`,
          height: '36px', // 36px + 2px borderTop + 2px borderBottom = 40px
        }}
        {...inputProps}
      />
      <StyledSelectArrow />
      <ShadowSpan
        aria-hidden="true"
        ref={(node: HTMLSpanElement | null): void => {
          shadowSpanRef.current = node;
        }}
      >
        {inputValue}
      </ShadowSpan>
      {isExpanded && isFocused && (
        <AutocompleteDropdown
          id={mainPopUpId}
          focusedIndex={focusedDropdownIndex}
          noOptionsAvailableFor={noOptionsAvailable}
          onOptionClick={handleOptionClick}
          onOptionMouseOver={(): void => {}}
          options={optionsToShow}
          ariaOwns={ariaOwns}
          ariaActivedescendant={focusedId}
        />
      )}
    </BorderWrapper>
  );
};

export default AutocompleteMultiSelect;
